// src/api.js

import { auth } from "./firebase";

const BASE_URL =
    process.env.REACT_APP_IS_LOCAL === "true"
        ? "http://localhost:8080"
        : "https://backend.rugbycore.com";

async function getAuthToken() {
    const currentUser = auth.currentUser;
    if (currentUser) {
        return await currentUser.getIdToken(true);
    }
    return null;
}

async function apiRequest(endpoint, method = "GET", data = null) {
    const token = await getAuthToken();
    const headers = { Authorization: `Bearer ${token}` };
    if (data) {
        headers["Content-Type"] = "application/json";
    }
    const response = await fetch(`${BASE_URL}${endpoint}`, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
    }
    const jsonData = await response.json();

    // Check if the response indicates fallback data is being used
    if (jsonData.usingFallback === true) {
        sessionStorage.setItem('usingFallbackData', 'true');
        // Dispatch custom event to notify components
        window.dispatchEvent(new CustomEvent('fallbackDataChanged', { detail: { usingFallback: true } }));
    } else if (jsonData.usingFallback === false) {
        sessionStorage.removeItem('usingFallbackData');
        // Dispatch custom event to notify components
        window.dispatchEvent(new CustomEvent('fallbackDataChanged', { detail: { usingFallback: false } }));
    }

    return jsonData;
}

export function getUsers() {
    return apiRequest("/admin/users", "GET");
}

export function getClubs() {
    return apiRequest("/admin/clubs", "GET");
}

export function createUser(data) {
    return apiRequest("/admin/createUser", "POST", data);
}

export function createClub(data) {
    return apiRequest("/admin/createClub", "POST", data);
}

export function updateClub(id, data) {
    return apiRequest(`/admin/clubs/${id}`, "PUT", data);
}

export function updateUser(uid, data) {
    return apiRequest(`/admin/users/${uid}`, "PUT", data);
}

export function deleteUser(uid) {
    return apiRequest(`/admin/users/${uid}`, "DELETE");
}

export function setUserLockStatus(uid, disabled) {
    return apiRequest(`/admin/users/${uid}/lock`, "PUT", { disabled });
}

export function generatePasswordResetLink(uid, email) {
    return apiRequest(`/admin/users/${uid}/reset-link`, "POST", { email });
}

export function deleteClub(id) {
    return apiRequest(`/admin/clubs/${id}`, "DELETE");
}

export function getFixtures(season) {
    const url = season ? `/api/matches/fixtures?season=${season}` : "/api/matches/fixtures";
    return apiRequest(url, "GET");
}

export function getResults(season) {
    const url = season ? `/api/matches/results?season=${season}` : "/api/matches/results";
    return apiRequest(url, "GET");
}

export function getLatestMatches(season) {
    const url = season ? `/api/matches/latest?season=${season}` : "/api/matches/latest";
    return apiRequest(url, "GET");
}

export function getAILLeagueTable() {
    return apiRequest("/api/matches/ail-league-table", "GET");
}

export function getUserDetails() {
    return apiRequest("/admin/userDetails", "GET");
}


// -- Resources --
export function getResources() {
    return apiRequest("/api/resources", "GET");
}

export function createResource(data) {
    return apiRequest("/api/resources", "POST", data);
}

export function updateResource(id, data) {
    return apiRequest(`/api/resources/${id}`, "PUT", data);
}

export function deleteResource(id) {
    return apiRequest(`/api/resources/${id}`, "DELETE");
}

// -- Bookings --
export function getResourceBookings() {
    return apiRequest("/api/resources/bookings", "GET");
}

export function createResourceBooking(data) {
    return apiRequest("/api/resources/bookings", "POST", data);
}

export function deleteResourceBooking(id) {
    return apiRequest(`/api/resources/bookings/${id}`, "DELETE");
}

export function approveResourceBooking(id, status) {
    return apiRequest(`/api/resources/bookings/${id}/approve`, "POST", { status });
}


export function getTeams() {
    return apiRequest("/api/teams", "GET");
}

export function createTeam(data) {
    return apiRequest("/api/teams", "POST", data);
}

export function updateTeam(id, data) {
    return apiRequest(`/api/teams/${id}`, "PUT", data);
}

export function deleteTeam(id) {
    return apiRequest(`/api/teams/${id}`, "DELETE");
}


// -- Volunteer Roles --
export function getVolunteerRoles() {
    return apiRequest("/api/volunteerRoles", "GET");
}

export function createVolunteerRole(data) {
    return apiRequest("/api/volunteerRoles", "POST", data);
}

export function updateVolunteerRole(id, data) {
    return apiRequest(`/api/volunteerRoles/${id}`, "PUT", data);
}

export function deleteVolunteerRole(id) {
    return apiRequest(`/api/volunteerRoles/${id}`, "DELETE");
}

// -- Access Logs --
export function logAccess(data) {
    return apiRequest("/admin/access-logs", "POST", data);
}

export function getAccessLogs(filters = {}) {
    const params = new URLSearchParams();
    if (filters.club) params.append("club", filters.club);
    if (filters.user) params.append("user", filters.user);
    if (filters.excludeUsers) params.append("excludeUsers", filters.excludeUsers);
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.limit) params.append("limit", filters.limit);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    return apiRequest(`/admin/access-logs${queryString}`, "GET");
}
